import React from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentCardBrandIcon, Tooltip } from 'components';
import { Headline2 } from 'pages/UserProfile/Header/styled';
import { UserSubscription } from 'types';
import { formatSubscriptionDate } from 'utils/dates';
import { formatMoney } from 'utils/money';
import { parseStripePaymentMethod } from 'utils/stripePaymentMethod';

import SwitchBillingModeLink from './SwitchBillingModeLink';
import {
  IconCardGeneric,
  IconCycle,
  IconInvoiceDoc,
  IconOpen,
  IconPerson,
} from './icons';
import {
  BillToEmail,
  BillToEmails,
  BillToLine,
  BillToMore,
  BtnOutline,
  Card,
  PaymentBlock,
  PaymentCopy,
  PaymentIconWrap,
  PaymentLabel,
  PaymentRow,
  PaymentValue,
  TextLink,
} from './styled';

const mutedHintStyle: React.CSSProperties = {
  margin: '6px 0 0',
  fontSize: 11,
  color: '#64748b',
  fontWeight: 500,
};

const linkHintStyle: React.CSSProperties = {
  fontSize: 11,
  marginTop: 4,
  display: 'inline-block',
};

type LastInvoiceMeta = {
  amount: number;
  currency: string;
  paidAt: string;
} | null;

type PaymentDetailsCardProps = {
  subscription: UserSubscription | null | undefined;
  isInvoiceBillingLayout: boolean;
  showBillingPaymentDetails: boolean;
  showSwitchBillingMode: boolean;
  isChfCurrency: boolean;
  canUseManageBilling: boolean;
  billingPortalUrl?: string;
  billingCyclePrimaryText: string;
  translatedPaymentCycle: string;
  paymentMethodLine: string | null;
  cardExpiry: { month: number; year: number } | null;
  lastInvoiceMeta: LastInvoiceMeta;
  lastInvoicePdfUrl?: string;
  invoiceContactEmail?: string;
  invoicePayerEmails?: string[];
  invoiceDefaultEmail?: string;
  onViewInvoices: () => void;
};

const LastInvoiceRow = ({
  lastInvoiceMeta,
  lastInvoicePdfUrl,
  billingPortalUrl,
  hintKey,
  language,
}: {
  lastInvoiceMeta: LastInvoiceMeta;
  lastInvoicePdfUrl?: string;
  billingPortalUrl?: string;
  hintKey: string;
  language: string;
}) => {
  const { t } = useTranslation();

  return (
    <PaymentRow>
      <PaymentIconWrap>
        <IconInvoiceDoc />
      </PaymentIconWrap>
      <PaymentCopy>
        <PaymentLabel>
          {t('subscriptions.profile.payment.lastInvoiceLabel')}
        </PaymentLabel>
        <PaymentValue style={{ fontSize: 13 }}>
          {lastInvoiceMeta
            ? `${formatMoney(
                lastInvoiceMeta.currency,
                lastInvoiceMeta.amount,
              )} · ${formatSubscriptionDate(lastInvoiceMeta.paidAt, language)}`
            : t('subscriptions.profile.payment.lastInvoiceUnavailable')}
        </PaymentValue>
        <p style={mutedHintStyle}>{t(hintKey)}</p>
        {lastInvoicePdfUrl || billingPortalUrl ? (
          <TextLink
            href={lastInvoicePdfUrl || billingPortalUrl || '#'}
            target="_blank"
            rel="noreferrer"
            style={linkHintStyle}
          >
            {t('subscriptions.profile.payment.downloadInvoice')}
          </TextLink>
        ) : null}
      </PaymentCopy>
    </PaymentRow>
  );
};

const BillToHint = ({
  payerEmails,
  defaultEmail,
  fallbackEmail,
}: {
  payerEmails: string[];
  defaultEmail?: string;
  fallbackEmail?: string;
}) => {
  const { t } = useTranslation();
  const firstPayer = payerEmails[0];
  const remainingEmails = [
    ...payerEmails.slice(1),
    ...(defaultEmail ? [defaultEmail] : []),
  ];
  const visibleEmail = firstPayer || defaultEmail || fallbackEmail;
  if (!visibleEmail) return null;

  return (
    <BillToLine>
      {t('subscriptions.profile.payment.sentToBillingLabel')}
      <BillToEmails>
        <BillToEmail>{visibleEmail}</BillToEmail>
        {firstPayer && remainingEmails.length > 0 ? (
          <Tooltip text={remainingEmails.join('\n')} multiline>
            <BillToMore>+{remainingEmails.length}</BillToMore>
          </Tooltip>
        ) : null}
      </BillToEmails>
    </BillToLine>
  );
};

const PaymentDetailsCard = ({
  subscription,
  isInvoiceBillingLayout,
  showBillingPaymentDetails,
  showSwitchBillingMode,
  isChfCurrency,
  canUseManageBilling,
  billingPortalUrl,
  billingCyclePrimaryText,
  translatedPaymentCycle,
  paymentMethodLine,
  cardExpiry,
  lastInvoiceMeta,
  lastInvoicePdfUrl,
  invoiceContactEmail,
  invoicePayerEmails = [],
  invoiceDefaultEmail,
  onViewInvoices,
}: PaymentDetailsCardProps) => {
  const { t, i18n } = useTranslation();
  const enterpriseAccountManagerName =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_NAME || '';
  const enterpriseAccountManagerEmail =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_EMAIL || '';

  return (
    <Card>
      <Headline2 style={{ marginBottom: '24px' }}>
        {t('subscriptions.profile.payment.title')}
      </Headline2>
      <PaymentBlock>
        {isInvoiceBillingLayout ? (
          <>
            <PaymentRow>
              <PaymentIconWrap>
                <IconInvoiceDoc />
              </PaymentIconWrap>
              <PaymentCopy>
                <PaymentLabel>
                  {t('subscriptions.profile.payment.methodLabel')}
                </PaymentLabel>
                <PaymentValue>
                  {t('subscriptions.profile.payment.invoiceMethodTitle')}
                </PaymentValue>
                <BillToHint
                  payerEmails={invoicePayerEmails}
                  defaultEmail={invoiceDefaultEmail}
                  fallbackEmail={invoiceContactEmail}
                />
                {showSwitchBillingMode && subscription ? (
                  <SwitchBillingModeLink subscription={subscription} />
                ) : null}
              </PaymentCopy>
            </PaymentRow>

            <PaymentRow>
              <PaymentIconWrap>
                <IconCycle />
              </PaymentIconWrap>
              <PaymentCopy>
                <PaymentLabel>
                  {t('subscriptions.profile.payment.cycleLabel')}
                </PaymentLabel>
                <PaymentValue>{billingCyclePrimaryText}</PaymentValue>
              </PaymentCopy>
            </PaymentRow>

            {enterpriseAccountManagerName || enterpriseAccountManagerEmail ? (
              <PaymentRow>
                <PaymentIconWrap>
                  <IconPerson />
                </PaymentIconWrap>
                <PaymentCopy>
                  <PaymentLabel>
                    {t('subscriptions.profile.payment.accountManagerLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {enterpriseAccountManagerName || '-'}
                  </PaymentValue>
                  {enterpriseAccountManagerEmail ? (
                    <TextLink
                      href={`mailto:${enterpriseAccountManagerEmail}`}
                      style={{
                        fontSize: 11,
                        marginTop: 6,
                        display: 'inline-block',
                      }}
                    >
                      {enterpriseAccountManagerEmail}
                    </TextLink>
                  ) : null}
                </PaymentCopy>
              </PaymentRow>
            ) : null}

            <LastInvoiceRow
              lastInvoiceMeta={lastInvoiceMeta}
              lastInvoicePdfUrl={lastInvoicePdfUrl}
              billingPortalUrl={billingPortalUrl}
              hintKey="subscriptions.profile.payment.lastInvoiceHintInvoice"
              language={i18n.language}
            />
          </>
        ) : (
          <>
            <PaymentRow>
              {showBillingPaymentDetails ? (
                <PaymentIconWrap $card>
                  <PaymentCardBrandIcon
                    brand={
                      parseStripePaymentMethod(
                        subscription?.billingPaymentDetails,
                      )?.card?.brand
                    }
                  />
                </PaymentIconWrap>
              ) : (
                <PaymentIconWrap>
                  <IconCardGeneric />
                </PaymentIconWrap>
              )}
              <PaymentCopy>
                <PaymentLabel>
                  {t('subscriptions.profile.payment.methodLabel')}
                </PaymentLabel>
                <PaymentValue>
                  {showBillingPaymentDetails
                    ? paymentMethodLine ||
                      t('subscriptions.profile.payment.methodOnFile')
                    : t('subscriptions.profile.payment.noPaymentYet')}
                </PaymentValue>
                {showBillingPaymentDetails && cardExpiry ? (
                  <p style={mutedHintStyle}>
                    {t('subscriptions.profile.payment.cardExpiresShort', {
                      month: String(cardExpiry.month).padStart(2, '0'),
                      year: String(cardExpiry.year),
                    })}
                  </p>
                ) : null}
                {showSwitchBillingMode && subscription ? (
                  <SwitchBillingModeLink subscription={subscription} />
                ) : null}
              </PaymentCopy>
            </PaymentRow>

            <PaymentRow>
              <PaymentIconWrap>
                <IconCycle />
              </PaymentIconWrap>
              <PaymentCopy>
                <PaymentLabel>
                  {t('subscriptions.profile.payment.cycleLabel')}
                </PaymentLabel>
                <PaymentValue>
                  {showBillingPaymentDetails && billingCyclePrimaryText
                    ? billingCyclePrimaryText
                    : showBillingPaymentDetails && translatedPaymentCycle
                    ? translatedPaymentCycle
                    : t('subscriptions.profile.payment.noActiveSubscription')}
                </PaymentValue>
              </PaymentCopy>
            </PaymentRow>

            {showBillingPaymentDetails ? (
              <LastInvoiceRow
                lastInvoiceMeta={lastInvoiceMeta}
                lastInvoicePdfUrl={lastInvoicePdfUrl}
                billingPortalUrl={billingPortalUrl}
                hintKey="subscriptions.profile.payment.lastInvoiceHint"
                language={i18n.language}
              />
            ) : null}
          </>
        )}
      </PaymentBlock>
      <div
        style={{
          marginTop: 'auto',
          paddingTop: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {isChfCurrency ? (
          <BtnOutline as="button" type="button" onClick={onViewInvoices}>
            {t('subscriptions.profile.payment.viewInvoices')}
          </BtnOutline>
        ) : canUseManageBilling ? (
          <BtnOutline href={billingPortalUrl} target="_blank" rel="noreferrer">
            <IconOpen />
            {t('subscriptions.profile.payment.manageBilling')}
          </BtnOutline>
        ) : (
          <BtnOutline as="button" type="button" disabled aria-disabled="true">
            <IconOpen />
            {t('subscriptions.profile.payment.manageBilling')}
          </BtnOutline>
        )}
      </div>
    </Card>
  );
};

export default PaymentDetailsCard;
